/**
 * Flow weather condition creation.
 * Duplicated from Scenes for Flow independence.
 */

const UI_WEATHER_TO_SCENE_MAPPING: Record<string, string> = {
	clouds: 'cc',
	wind_speed: 'w',
	wind_force: 'wf',
	wind_direction: 'dd',
	temperature: 't',
	humidity: 'h',
	chance_precipitation: 'pop',
};

const SCENE_WEATHER_VALUE_TO_UI: Record<string, string> = {
	clear: 'clear_sky',
	few: 'partly_cloudy',
	scattered: 'mostly_cloudy',
	broken: 'cloudy',
	overcast: 'overcast',
	N: 'north',
	NNE: 'north-northeast',
	NE: 'northeast',
	ENE: 'east-northeast',
	E: 'east',
	ESE: 'east-southeast',
	SE: 'southeast',
	SSE: 'south-southeast',
	S: 'south',
	SSW: 'south-southwest',
	SW: 'southwest',
	WSW: 'west-southwest',
	W: 'west',
	WNW: 'west-northwest',
	NW: 'northwest',
	NNW: 'north-northwest',
	wf0: 'calm',
	wf1: 'light_air',
	wf2: 'light_breeze',
	wf3: 'gentle_breeze',
	wf4: 'moderate_breeze',
	wf5: 'fresh_breeze',
	wf6: 'strong_breeze',
	wf7: 'near_gale',
	wf8: 'gale',
	wf9: 'strong_gale',
	wf10: 'storm',
	wf11: 'violent_storm',
	wf12: 'hurricane',
};

const WEATHER_API_CODES_BY_STR: Record<string, string> = {
	calm: 'wf0',
	light_air: 'wf1',
	light_breeze: 'wf2',
	gentle_breeze: 'wf3',
	moderate_breeze: 'wf4',
	fresh_breeze: 'wf5',
	strong_breeze: 'wf6',
	near_gale: 'wf7',
	gale: 'wf8',
	strong_gale: 'wf9',
	storm: 'wf10',
	violent_storm: 'wf11',
	hurricane: 'wf12',
	clear: 'clear_sky',
	few: 'partly_cloudy',
	scattered: 'mostly_cloudy',
	broken: 'cloudy',
	overcast: 'overcast',
	north: 'N',
	north_northeast: 'NNE',
	northeast: 'NE',
	east_northeast: 'ENE',
	east: 'E',
	east_southeast: 'ESE',
	southeast: 'SE',
	south_southeast: 'SSE',
	south: 'S',
	south_southwest: 'SSW',
	southwest: 'SW',
	west_southwest: 'WSW',
	west: 'W',
	west_northwest: 'WNW',
	northwest: 'NW',
	north_northwest: 'NNW',
};

function convertUiWeatherToSceneCondition(
	cond: string,
	op: string,
	val: unknown,
): { condition: string; value: unknown; operator: string } {
	let operator = 'eq';
	if (op !== 'equal') {
		operator = op === 'less_than' ? 'lt' : 'gt';
	}

	switch (cond) {
		case 'clouds':
			return {
				condition: UI_WEATHER_TO_SCENE_MAPPING['clouds'] || 'cc',
				value:
					Object.keys(SCENE_WEATHER_VALUE_TO_UI)[
						Object.values(SCENE_WEATHER_VALUE_TO_UI).indexOf(String(val))
					] ?? val,
				operator: 'eq',
			};
		case 'wind_direction':
			return {
				condition: UI_WEATHER_TO_SCENE_MAPPING['wind_direction'] || 'dd',
				value: WEATHER_API_CODES_BY_STR[String(val).replace(/-/g, '_')] ?? val,
				operator: 'eq',
			};
		case 'wind_force':
			return {
				condition: UI_WEATHER_TO_SCENE_MAPPING['wind_force'] || 'wf',
				value: WEATHER_API_CODES_BY_STR[String(val).replace(/\s/g, '_')] ?? val,
				operator,
			};
		case 'chance_precipitation':
			return {
				condition: UI_WEATHER_TO_SCENE_MAPPING['chance_precipitation'] || 'pop',
				value: Number(val),
				operator,
			};
		case 'temperature':
		case 'humidity':
			return {
				condition: UI_WEATHER_TO_SCENE_MAPPING[cond] || cond,
				value: Number(val),
				operator,
			};
		default:
			return {
				condition: UI_WEATHER_TO_SCENE_MAPPING[cond] || cond,
				value: typeof val === 'number' ? val : Number(val) || val,
				operator,
			};
	}
}

export interface FlowWeatherConditionInput {
	cond: string;
	op: string;
	value: unknown;
	locid: string;
	hours: number;
	execution_type?: 'once' | 'any';
}

export function createWeatherConditionFromFlow(
	input: FlowWeatherConditionInput,
): Record<string, unknown> | null {
	const { condition, operator, value } = convertUiWeatherToSceneCondition(
		input.cond,
		input.op,
		input.value,
	);
	if (!condition || value === undefined || value === '') return null;

	const concatCondition = `owm:${input.locid}+${input.hours ?? 0}:${condition}`;

	if (input.execution_type === 'any') {
		return {
			[operator]: [{ rr: concatCondition }, value],
			_gui_purpose: 'condition',
		};
	}
	return {
		rr: concatCondition,
		thsop: operator,
		thsval: value,
		_gui_purpose: 'condition',
	};
}
